import { useState, useEffect, Fragment, useRef, useCallback } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { CalendarDaysIcon, ClockIcon } from "@heroicons/react/24/outline";
import { parseAbsoluteToLocal } from "@internationalized/date";
import TimePicker from "./TimePicker"; // Import the refactored TimePicker

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MOCK_CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = MOCK_CURRENT_YEAR - 150;
const MAX_YEAR = MOCK_CURRENT_YEAR;
const YEARS_IN_BLOCK = 12; // For the years view

const isValidDate = (year, month, day) => {
  const date = new Date(year, month, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month &&
    date.getDate() === day
  );
};

const isValidYearRange = (year) => year >= MIN_YEAR && year <= MAX_YEAR;

const parseYYYYMMDD = (dateString) => {
  if (!dateString) return null;

  const yyyyMmDdRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
  const match = String(dateString).match(yyyyMmDdRegex);

  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1; // 0-based month
  const day = parseInt(match[3], 10);

  if (!isValidDate(year, month, day) || !isValidYearRange(year)) {
    return null;
  }

  return new Date(year, month, day);
};

const parseDDMMYYYY = (dateString) => {
  if (!dateString) return null;

  const parts = dateString.split("/");
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // 0-based month
  const year = parseInt(parts[2], 10);

  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  if (!isValidDate(year, month, day) || !isValidYearRange(year)) return null;

  return new Date(year, month, day);
};

const formatToYYYYMMDD = (date) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const validateDate = (value, showTime = false) => {
  if (!value) return true;

  if (showTime) {
    // Validate datetime format
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return "Invalid date and time format. Use DD/MM/YYYY HH:MM format.";
    }

    if (!isValidYearRange(date.getFullYear())) {
      return `Year must be between ${MIN_YEAR} and ${MAX_YEAR}.`;
    }

    return true;
  } else {
    const date = parseYYYYMMDD(value);
    if (!date) {
      return "Invalid date format. Use DD/MM/YYYY or select from calendar.";
    }

    return true;
  }
};

export default function DatePicker({
  label = "Date of Birth",
  name,
  value,
  onChange,
  onBlur,
  error,
  darkMode = true,
  showTime = false,
  required = false,
}) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(
    parseAbsoluteToLocal(new Date().toISOString())
  );
  const [viewDate, setViewDate] = useState(new Date());
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [calendarView, setCalendarView] = useState("days"); // 'days', 'months', 'years'
  const [activeTab, setActiveTab] = useState("date"); // 'date', 'time'
  const [yearBlockStart, setYearBlockStart] = useState(
    Math.floor(new Date().getFullYear() / YEARS_IN_BLOCK) * YEARS_IN_BLOCK
  );
  const popoverButtonRef = useRef(null);
  const popoverPanelRef = useRef(null);

  useEffect(() => {
    if (!value) {
      setSelectedDate(null);
      setInputValue("");
      const today = new Date();
      setViewDate(today);
      setSelectedTime(parseAbsoluteToLocal(today.toISOString())); // Reset time too
      setCalendarView("days");
      setYearBlockStart(
        Math.floor(today.getFullYear() / YEARS_IN_BLOCK) * YEARS_IN_BLOCK
      );
      return;
    }

    if (showTime) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setSelectedDate(date);
        setViewDate(
          new Date(date.getFullYear(), date.getMonth(), date.getDate())
        ); // viewDate should be just date part
        setSelectedTime(parseAbsoluteToLocal(date.toISOString()));
        setInputValue(
          `${date.toLocaleDateString("en-GB")} ${date.toLocaleTimeString(
            "en-GB",
            {
              hour: "2-digit",
              minute: "2-digit",
            }
          )}`
        );
        setCalendarView("days");
        setYearBlockStart(
          Math.floor(date.getFullYear() / YEARS_IN_BLOCK) * YEARS_IN_BLOCK
        );
      } else {
        setSelectedDate(null);
        // Attempt to parse as DD/MM/YYYY HH:MM for inputValue display if invalid ISO
        const typedValueMatch = value.match(
          /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/
        );
        if (typedValueMatch) {
          setInputValue(value);
        } else {
          setInputValue("Invalid Date");
        }
      }
    } else {
      const date = parseYYYYMMDD(value);
      if (date) {
        setSelectedDate(date);
        setViewDate(date);
        setInputValue(date.toLocaleDateString("en-GB"));
        setCalendarView("days");
        setYearBlockStart(
          Math.floor(date.getFullYear() / YEARS_IN_BLOCK) * YEARS_IN_BLOCK
        );
      } else {
        setSelectedDate(null);
        setInputValue(value); // Keep user's typed input if not parsable by parseYYYYMMDD
      }
    }
  }, [value, showTime]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        popoverButtonRef.current &&
        !popoverButtonRef.current.contains(event.target) &&
        popoverPanelRef.current &&
        !popoverPanelRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleLocalInputChange = useCallback((event) => {
    setInputValue(event.target.value);
  }, []);

  const handleLocalBlur = useCallback(() => {
    const typedValue = inputValue.trim();
    if (!typedValue) {
      onChange(""); // Clear value if input is empty
      if (onBlur) onBlur();
      return;
    }

    if (showTime) {
      const datetimeRegex =
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/;
      const match = typedValue.match(datetimeRegex);
      if (match) {
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1; // JS months are 0-based
        const year = parseInt(match[3], 10);
        const hour = parseInt(match[4], 10);
        const minute = parseInt(match[5], 10);

        if (
          isValidDate(year, month, day) &&
          isValidYearRange(year) &&
          hour >= 0 &&
          hour <= 23 &&
          minute >= 0 &&
          minute <= 59
        ) {
          const date = new Date(year, month, day, hour, minute);
          onChange(date.toISOString());
        } else {
          onChange(typedValue); // Invalid date/time components, pass raw value
        }
      } else {
        onChange(typedValue); // Format doesn't match, pass raw value
      }
    } else {
      const date = parseDDMMYYYY(typedValue);
      if (date) {
        onChange(formatToYYYYMMDD(date));
      } else {
        onChange(typedValue); // Format doesn't match, pass raw value
      }
    }
    if (onBlur) onBlur();
  }, [inputValue, onChange, onBlur, showTime]);

  const handleDateSelectFromCalendar = useCallback(
    (dateFromCalendar) => {
      let finalDate;
      if (showTime) {
        const timePart =
          selectedTime || parseAbsoluteToLocal(new Date().toISOString()); // Default to current time if none selected
        finalDate = new Date(
          dateFromCalendar.getFullYear(),
          dateFromCalendar.getMonth(),
          dateFromCalendar.getDate(),
          timePart.hour,
          timePart.minute
        );
        setSelectedDate(finalDate);
        setViewDate(
          new Date(
            dateFromCalendar.getFullYear(),
            dateFromCalendar.getMonth(),
            dateFromCalendar.getDate()
          )
        );
        setInputValue(
          `${finalDate.toLocaleDateString(
            "en-GB"
          )} ${finalDate.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
          })}`
        );
        onChange(finalDate.toISOString());
      } else {
        finalDate = dateFromCalendar;
        setSelectedDate(finalDate);
        setViewDate(finalDate);
        setInputValue(finalDate.toLocaleDateString("en-GB"));
        onChange(formatToYYYYMMDD(finalDate));
      }
      setCalendarView("days");

      if (!showTime) {
        setIsOpen(false);
        if (popoverButtonRef.current) {
          popoverButtonRef.current.focus();
        }
      }
    },
    [onChange, showTime, selectedTime]
  );

  const handleTimeChange = useCallback(
    (newTimeValue) => {
      setSelectedTime(newTimeValue);
      if (selectedDate) {
        // Date part must exist
        const currentSelectedDate = new Date(selectedDate);
        const combinedDateTime = new Date(
          currentSelectedDate.getFullYear(),
          currentSelectedDate.getMonth(),
          currentSelectedDate.getDate(),
          newTimeValue.hour,
          newTimeValue.minute
        );
        setSelectedDate(combinedDateTime); // Update selectedDate to reflect new time
        setInputValue(
          `${combinedDateTime.toLocaleDateString(
            "en-GB"
          )} ${combinedDateTime.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
          })}`
        );
        onChange(combinedDateTime.toISOString());
      } else {
        // If no date is selected yet, but time is changed, we might want to set a default date (e.g., today)
        // Or just store the time and combine it when a date is picked.
        // For now, inputValue won't update if selectedDate is null. This is fine as date selection will combine.
      }
    },
    [selectedDate, onChange]
  );

  const goToPreviousMonth = useCallback(() => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }, []);

  const goToNextMonth = useCallback(() => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }, []);

  const goToPreviousYear = useCallback(() => {
    setViewDate((prev) => new Date(prev.getFullYear() - 1, prev.getMonth(), 1));
  }, []);

  const goToNextYear = useCallback(() => {
    setViewDate((prev) => new Date(prev.getFullYear() + 1, prev.getMonth(), 1));
  }, []);

  const goToPreviousYearBlock = useCallback(() => {
    setYearBlockStart((prev) => prev - YEARS_IN_BLOCK);
  }, []);

  const goToNextYearBlock = useCallback(() => {
    setYearBlockStart((prev) => prev + YEARS_IN_BLOCK);
  }, []);

  const handleHeaderClick = () => {
    if (calendarView === "days") {
      setCalendarView("months");
    } else if (calendarView === "months") {
      setYearBlockStart(
        Math.floor(viewDate.getFullYear() / YEARS_IN_BLOCK) * YEARS_IN_BLOCK
      );
      setCalendarView("years");
    }
  };

  const handleMonthSelect = (monthIndex) => {
    setViewDate(new Date(viewDate.getFullYear(), monthIndex, 1));
    setCalendarView("days");
  };

  const handleYearSelect = (year) => {
    setViewDate(new Date(year, viewDate.getMonth(), 1));
    setCalendarView("months");
  };

  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

  const firstDayOfMonth = (year, month) => {
    let day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const currentYear = viewDate.getFullYear();
  const currentMonth = viewDate.getMonth();
  const dayHeaders = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const canGoToPrev = useCallback(() => {
    if (calendarView === "days") {
      return !(currentYear === MIN_YEAR && currentMonth === 0);
    }
    if (calendarView === "months") {
      return currentYear > MIN_YEAR;
    }
    if (calendarView === "years") {
      return yearBlockStart > MIN_YEAR;
    }
    return false;
  }, [calendarView, currentYear, currentMonth, yearBlockStart]);

  const canGoToNext = useCallback(() => {
    if (calendarView === "days") {
      return !(currentYear === MAX_YEAR && currentMonth === 11);
    }
    if (calendarView === "months") {
      return currentYear < MAX_YEAR;
    }
    if (calendarView === "years") {
      return yearBlockStart + YEARS_IN_BLOCK <= MAX_YEAR + (YEARS_IN_BLOCK - 1);
    }
    return false;
  }, [calendarView, currentYear, currentMonth, yearBlockStart]);

  const handleNavigationPrevious = () => {
    if (calendarView === "days") goToPreviousMonth();
    else if (calendarView === "months") goToPreviousYear();
    else if (calendarView === "years") goToPreviousYearBlock();
  };

  const handleNavigationNext = () => {
    if (calendarView === "days") goToNextMonth();
    else if (calendarView === "months") goToNextYear();
    else if (calendarView === "years") goToNextYearBlock();
  };

  const getHeaderText = () => {
    if (calendarView === "days") {
      return `${monthNames[currentMonth]} ${currentYear}`;
    }
    if (calendarView === "months") {
      return `${currentYear}`;
    }
    if (calendarView === "years") {
      const endYear = yearBlockStart + YEARS_IN_BLOCK - 1;
      return `${yearBlockStart} - ${endYear > MAX_YEAR ? MAX_YEAR : endYear}`;
    }
    return "";
  };

  const renderDaysView = () => {
    const dayCells = [];
    const today = new Date();
    const todayString = today.toDateString();

    const numDaysInCurrentMonth = daysInMonth(currentYear, currentMonth);
    const firstDayIndexOfMonth = firstDayOfMonth(currentYear, currentMonth);

    for (let i = 0; i < firstDayIndexOfMonth; i++) {
      dayCells.push(
        <div key={`blank-start-${i}`} className="p-1.5 text-center"></div>
      );
    }

    for (let day = 1; day <= numDaysInCurrentMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const isSelectedCal =
        selectedDate &&
        date.toDateString() === new Date(selectedDate).toDateString(); // Ensure comparison with Date object
      const isToday = date.toDateString() === todayString;
      const isDisabled =
        date.getFullYear() < MIN_YEAR ||
        date.getFullYear() > MAX_YEAR ||
        (date.getFullYear() === MIN_YEAR &&
          date.getMonth() < new Date(MIN_YEAR, 0, 1).getMonth()) || // More precise min date check
        (date.getFullYear() === MAX_YEAR &&
          date.getMonth() > new Date(MAX_YEAR, 11, 31).getMonth()); // More precise max date check

      dayCells.push(
        <button
          type="button"
          key={day}
          disabled={isDisabled}
          onClick={() => handleDateSelectFromCalendar(date)}
          className={`p-1.5 text-sm rounded-md text-center focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors
            ${
              isDisabled
                ? `${
                    darkMode ? "dark:text-gray-600" : "text-gray-400"
                  } cursor-not-allowed`
                : `hover:bg-blue-100 ${
                    darkMode ? "dark:hover:bg-gray-600" : ""
                  }`
            }
            ${
              isSelectedCal
                ? `bg-blue-600 text-white hover:bg-blue-700 ${
                    darkMode ? "dark:bg-blue-500 dark:hover:bg-blue-400" : ""
                  }`
                : ""
            }
            ${
              !isSelectedCal && isToday
                ? `font-semibold text-blue-600 border border-blue-500 ${
                    darkMode ? "dark:text-blue-400 dark:border-blue-400" : ""
                  }`
                : ""
            }
            ${
              !isSelectedCal && !isToday && !isDisabled
                ? `${darkMode ? "dark:text-gray-200" : "text-gray-700"}`
                : ""
            }
          `}
        >
          {day}
        </button>
      );
    }

    const totalCellsInGrid =
      Math.ceil((firstDayIndexOfMonth + numDaysInCurrentMonth) / 7) * 7;
    for (let i = dayCells.length; i < totalCellsInGrid; i++) {
      dayCells.push(
        <div key={`blank-end-${i}`} className="p-1.5 text-center"></div>
      );
    }
    return (
      <>
        <div className="grid grid-cols-7 gap-0.5 mb-2">
          {dayHeaders.map((day) => (
            <div
              key={day}
              className={`text-xs font-medium text-gray-500 text-center p-1.5 ${
                darkMode ? "dark:text-gray-400" : ""
              }`}
            >
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5">{dayCells}</div>
      </>
    );
  };

  const renderMonthsView = () => {
    return (
      <div className="grid grid-cols-3 gap-2 p-1">
        {monthNames.map((month, index) => {
          const isDisabled =
            (currentYear === MIN_YEAR &&
              index < new Date(MIN_YEAR, 0, 1).getMonth()) ||
            (currentYear === MAX_YEAR &&
              index > new Date(MAX_YEAR, 11, 31).getMonth());
          const isCurrentSelectedMonth =
            selectedDate &&
            new Date(selectedDate).getFullYear() === currentYear &&
            new Date(selectedDate).getMonth() === index;

          return (
            <button
              key={month}
              type="button"
              disabled={isDisabled}
              onClick={() => handleMonthSelect(index)}
              className={`p-2 text-sm rounded-md text-center focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors
                ${
                  isDisabled
                    ? `text-gray-400 cursor-not-allowed ${
                        darkMode ? "dark:text-gray-600" : ""
                      }`
                    : `hover:bg-blue-100 ${
                        darkMode
                          ? "dark:text-gray-200 dark:hover:bg-gray-600"
                          : "text-gray-700"
                      }`
                }
                ${
                  isCurrentSelectedMonth
                    ? `bg-blue-600 text-white ${
                        darkMode ? "dark:bg-blue-500" : ""
                      }`
                    : `${darkMode ? "dark:text-gray-200" : "text-gray-700"}`
                }
              `}
            >
              {month.substring(0, 3)}
            </button>
          );
        })}
      </div>
    );
  };

  const renderYearsView = () => {
    const years = [];
    for (let i = 0; i < YEARS_IN_BLOCK; i++) {
      const year = yearBlockStart + i;
      if (year > MAX_YEAR) break; // Don't show years beyond MAX_YEAR
      if (year < MIN_YEAR) continue; // Don't show years before MIN_YEAR in the block start logic adjustment

      const isDisabled = year < MIN_YEAR || year > MAX_YEAR;
      const isCurrentSelectedYear =
        selectedDate && new Date(selectedDate).getFullYear() === year;

      years.push(
        <button
          key={year}
          type="button"
          disabled={isDisabled}
          onClick={() => handleYearSelect(year)}
          className={`p-2 text-sm rounded-md text-center focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors
            ${
              isDisabled
                ? `text-gray-400 cursor-not-allowed ${
                    darkMode ? "dark:text-gray-600" : ""
                  }`
                : `hover:bg-blue-100 ${
                    darkMode
                      ? "dark:text-gray-200 dark:hover:bg-gray-600"
                      : "text-gray-700"
                  }`
            }
            ${
              isCurrentSelectedYear
                ? `bg-blue-600 text-white ${darkMode ? "dark:bg-blue-500" : ""}`
                : `${darkMode ? "dark:text-gray-200" : "text-gray-700"}`
            }
          `}
        >
          {year}
        </button>
      );
    }
    return <div className="grid grid-cols-3 gap-2 p-1">{years}</div>;
  };

  // NEW: Define Popover Width Class based on content
  const getPopoverWidthClass = () => {
    if (showTime && activeTab === "time") {
      return "w-auto max-w-[220px]"; // TimePicker is w-fit, give popover a max width for it
    }
    if (calendarView === "months" || calendarView === "years") {
      return "w-full sm:w-64"; // For month/year grids (approx 256px)
    }
    return "w-full sm:w-72"; // For day grid (approx 288px)
  };

  // MODIFIED: renderTimeTab to use imported TimePicker
  const renderTimeTab = () => {
    return (
      <TimePicker
        value={selectedTime}
        onChange={handleTimeChange}
        darkMode={darkMode}
      />
    );
  };

  return (
    <div className="flex flex-col w-full">
      <label
        htmlFor={`datepicker-input-${name}`}
        className={`text-sm mb-1 font-semibold text-gray-900 ${
          darkMode ? "dark:text-gray-200" : ""
        }`}
      >
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <Popover as="div" className="relative w-full">
        {() => (
          <>
            <div className="relative">
              <PopoverButton
                ref={popoverButtonRef}
                as="input"
                type="text"
                id={`datepicker-input-${name}`}
                name={name}
                value={inputValue}
                onChange={handleLocalInputChange}
                onBlur={handleLocalBlur}
                onClick={() => setIsOpen((prev) => !prev)}
                placeholder={showTime ? "DD/MM/YYYY HH:MM" : "DD/MM/YYYY"}
                className={`
                  w-full px-3 py-2 border rounded-lg text-left focus:ring-2 focus:outline-none
                  bg-white text-gray-900 placeholder:text-gray-500
                  ${
                    error
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-500 focus:ring-blue-500"
                  }
                  ${
                    darkMode
                      ? `
                    dark:bg-gray-700 dark:text-gray-200 dark:placeholder:text-gray-400
                    ${
                      error
                        ? "dark:border-red-500 dark:focus:ring-red-500"
                        : "dark:border-gray-600 dark:focus:ring-blue-500"
                    }
                  `
                      : ""
                  }
                `}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                {showTime ? (
                  <div className="flex items-center gap-1">
                    <CalendarDaysIcon
                      className={`w-4 h-4 text-gray-600 ${
                        darkMode ? "dark:text-gray-400" : ""
                      }`}
                    />
                    <ClockIcon
                      className={`w-4 h-4 text-gray-600 ${
                        darkMode ? "dark:text-gray-400" : ""
                      }`}
                    />
                  </div>
                ) : (
                  <CalendarDaysIcon
                    className={`w-5 h-5 text-gray-600 ${
                      darkMode ? "dark:text-gray-400" : ""
                    }`}
                  />
                )}
              </div>
            </div>
            <Transition
              as={Fragment}
              show={isOpen}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel
                ref={popoverPanelRef}
                static
                className={`
                  absolute z-20 mt-1 ${getPopoverWidthClass()} bg-white border border-gray-300 rounded-md shadow-lg p-3 focus:outline-none
                  ${darkMode ? "dark:bg-gray-800 dark:border-gray-700" : ""}
                `}
              >
                {showTime && (
                  <div className="flex border-b border-gray-200 dark:border-gray-700 mb-3">
                    <button
                      type="button"
                      onClick={() => setActiveTab("date")}
                      className={`flex-1 py-2 px-4 text-sm font-medium rounded-t-md transition-colors
                        ${
                          activeTab === "date"
                            ? `bg-blue-50 text-blue-700 border-b-2 border-blue-700 ${
                                darkMode
                                  ? "dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-400"
                                  : ""
                              }`
                            : `text-gray-500 hover:text-gray-700 ${
                                darkMode
                                  ? "dark:text-gray-400 dark:hover:text-gray-200"
                                  : ""
                              }`
                        }
                      `}
                    >
                      <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
                      Date
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab("time")}
                      className={`flex-1 py-2 px-4 text-sm font-medium rounded-t-md transition-colors
                        ${
                          activeTab === "time"
                            ? `bg-blue-50 text-blue-700 border-b-2 border-blue-700 ${
                                darkMode
                                  ? "dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-400"
                                  : ""
                              }`
                            : `text-gray-500 hover:text-gray-700 ${
                                darkMode
                                  ? "dark:text-gray-400 dark:hover:text-gray-200"
                                  : ""
                              }`
                        }
                      `}
                    >
                      <ClockIcon className="w-4 h-4 inline mr-1" />
                      Time
                    </button>
                  </div>
                )}

                {showTime && activeTab === "time" ? (
                  renderTimeTab() // Renders the new TimePicker component
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <button
                        type="button"
                        onClick={handleNavigationPrevious}
                        disabled={!canGoToPrev()}
                        className={`
                          p-1.5 rounded-full hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent 
                          focus:outline-none focus:ring-1 focus:ring-blue-400
                          ${
                            darkMode
                              ? `
                            dark:hover:bg-gray-700 
                            dark:disabled:text-gray-500 
                            dark:disabled:hover:bg-transparent
                          `
                              : ""
                          }
                        `}
                      >
                        <ChevronLeftIcon
                          className={`w-5 h-5 text-gray-600 ${
                            darkMode ? "dark:text-gray-400" : ""
                          }`}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={handleHeaderClick}
                        className={`
                          text-sm font-semibold text-gray-800 px-2 py-1 rounded-md hover:bg-gray-100
                          focus:outline-none focus:ring-1 focus:ring-blue-400
                          ${
                            darkMode
                              ? "dark:text-gray-200 dark:hover:bg-gray-700"
                              : ""
                          }
                        `}
                        disabled={calendarView === "years"}
                      >
                        {getHeaderText()}
                      </button>
                      <button
                        type="button"
                        onClick={handleNavigationNext}
                        disabled={!canGoToNext()}
                        className={`
                          p-1.5 rounded-full hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent 
                          focus:outline-none focus:ring-1 focus:ring-blue-400
                          ${
                            darkMode
                              ? `
                            dark:hover:bg-gray-700 
                            dark:disabled:text-gray-500 
                            dark:disabled:hover:bg-transparent
                          `
                              : ""
                          }
                        `}
                      >
                        <ChevronRightIcon
                          className={`w-5 h-5 text-gray-600 ${
                            darkMode ? "dark:text-gray-400" : ""
                          }`}
                        />
                      </button>
                    </div>

                    {/* MODIFIED: Container for date/month/year views for natural height */}
                    <div className="relative">
                      <Transition
                        as={Fragment}
                        show={calendarView === "days"}
                        enter="transition ease-out duration-150"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                      >
                        {/* Removed absolute inset-0 from inner div */}
                        <div>{renderDaysView()}</div>
                      </Transition>
                      <Transition
                        as={Fragment}
                        show={calendarView === "months"}
                        enter="transition ease-out duration-150"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                      >
                        <div>{renderMonthsView()}</div>
                      </Transition>
                      <Transition
                        as={Fragment}
                        show={calendarView === "years"}
                        enter="transition ease-out duration-150"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                      >
                        <div>{renderYearsView()}</div>
                      </Transition>
                    </div>
                  </>
                )}

                {/* Done button for when showTime is true */}
                {showTime && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => {
                        setIsOpen(false);
                        if (popoverButtonRef.current) {
                          popoverButtonRef.current.focus();
                        }
                      }}
                      className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
                    >
                      Done
                    </button>
                  </div>
                )}
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
      {error && <p className="text-red-500 text-xs mt-1">{error.message}</p>}
    </div>
  );
}
